import { api } from './api.ts';
import { withDoc } from './docs.ts';
import { attachmentBytes } from './attachments.ts';
import { deriveTitle } from './markdown.ts';
import { fromB64Url, newDataKey, openText, sealText, sodiumReady, toB64, toB64Url } from '../crypto/vault.ts';
import type { LocalNote } from './types.ts';

/**
 * Geteilte Notizen (docs/KONZEPT.md, Abschnitt 12).
 *
 * Ein Share-Link erzeugt clientseitig einen Snapshot, verschlüsselt ihn mit einem
 * **neuen** Zufallsschlüssel und lädt nur den Ciphertext hoch. Der Schlüssel steht im
 * URL-Fragment — alles hinter `#` sendet der Browser niemals an den Server.
 *
 * Snapshot heißt: Der geteilte Stand friert ein. Spätere Änderungen fließen erst mit
 * „Link aktualisieren" nach, damit nichts versehentlich nach außen wandert.
 */

export type SharePayload = {
  title: string;
  content: string;
  sharedAt: number;
};

/** Bilder werden eingebettet, damit ein geteilter Text nicht ohne sie ankommt. */
const MAX_IMAGE = 2 * 1024 * 1024;
const MAX_TOTAL = 6 * 1024 * 1024;

const ATTACHMENT = /(!?)\[([^\]\n]*)\]\(athene:([0-9a-f-]{36})\)/g;

function shareId(): string {
  const random = new Uint8Array(12);
  crypto.getRandomValues(random);
  return [...random].map((value) => value.toString(36).padStart(2, '0')).join('').slice(0, 16);
}

/** secret-Blöcke werden beim Erstellen des Snapshots grundsätzlich entfernt. */
function withoutSecrets(content: string): string {
  return content.replace(/^```secret[^\n]*\n[\s\S]*?^```\n?/gm, '');
}

async function buildSnapshot(note: LocalNote): Promise<SharePayload> {
  let content = withoutSecrets(note.content);
  let budget = MAX_TOTAL;

  for (const reference of [...content.matchAll(ATTACHMENT)]) {
    const id = reference[3] ?? '';
    const loaded = await attachmentBytes(note.id, id).catch(() => null);
    const isImage = reference[1] === '!' && loaded?.meta.mime.startsWith('image/');

    if (!loaded || !isImage || loaded.bytes.byteLength > MAX_IMAGE || loaded.bytes.byteLength > budget) {
      content = content.replace(
        reference[0],
        loaded ? `_${loaded.meta.name} — nicht mitgeteilt_` : '_Anhang fehlt_',
      );
      continue;
    }
    budget -= loaded.bytes.byteLength;
    content = content.replace(
      reference[0],
      `![${reference[2]}](data:${loaded.meta.mime};base64,${toB64(loaded.bytes)})`,
    );
  }

  return { title: deriveTitle(note.content), content, sharedAt: Date.now() };
}

export type CreatedShare = { id: string; key: string; url: string };

export async function createShare(
  note: LocalNote,
  options: { expiresInDays?: number | null; id?: string; key?: string } = {},
): Promise<CreatedShare> {
  await sodiumReady();
  const snapshot = await buildSnapshot(note);
  const key = options.key ? fromB64Url(options.key) : newDataKey();
  const id = options.id ?? shareId();
  const expiresAt =
    options.expiresInDays === null || options.expiresInDays === undefined
      ? null
      : Date.now() + options.expiresInDays * 86_400_000;

  await api.putShare(id, {
    noteId: note.id,
    payload: sealText(JSON.stringify(snapshot), key),
    expiresAt,
  });

  const keyText = toB64Url(key);
  return { id, key: keyText, url: `${location.origin}/s/${id}#k=${keyText}` };
}

export async function loadShare(id: string, keyText: string): Promise<SharePayload> {
  await sodiumReady();
  const response = await fetch(`/api/s/${id}`);
  if (response.status === 410) throw new Error('expired');
  if (!response.ok) throw new Error('not_found');
  const body = (await response.json()) as { payload: string };
  return JSON.parse(openText(body.payload, fromB64Url(keyText))) as SharePayload;
}

export type StoredShare = { id: string; key: string; expiresAt: number | null };

/**
 * Der Schlüssel steht nur im Fragment — der Server kennt ihn nicht. Damit „Kopieren"
 * und „Link aktualisieren" später noch funktionieren, merkt die Notiz ihn sich selbst.
 */
export async function storedShare(noteId: string): Promise<StoredShare | null> {
  return withDoc(noteId, (entry) => {
    const raw = entry.doc.getMap('share').get('link');
    return typeof raw === 'string' ? (JSON.parse(raw) as StoredShare) : null;
  });
}

async function rememberShare(noteId: string, share: StoredShare | null): Promise<void> {
  await withDoc(noteId, (entry) => {
    if (share) entry.doc.getMap('share').set('link', JSON.stringify(share));
    else entry.doc.getMap('share').delete('link');
  });
}

export async function shareNote(
  note: LocalNote,
  expiresInDays: number | null,
): Promise<CreatedShare> {
  const existing = await storedShare(note.id);
  const created = await createShare(note, {
    expiresInDays,
    id: existing?.id,
    key: existing?.key,
  });
  await rememberShare(note.id, {
    id: created.id,
    key: created.key,
    expiresAt: expiresInDays === null ? null : Date.now() + expiresInDays * 86_400_000,
  });
  return created;
}

export async function revokeShare(noteId: string, id: string): Promise<void> {
  await api.deleteShare(id).catch(() => undefined);
  await rememberShare(noteId, null);
}

export function shareUrl(share: StoredShare): string {
  return `${location.origin}/s/${share.id}#k=${share.key}`;
}

export const listShares = api.listShares;
